import { useLocation } from "react-router-dom";
import { navigationConfig } from "../config/navigationConfig";
import { NavigationState, UserMode } from "../types/navigation";

interface UseNavigationOptions {
  /** Whether the current user has admin role - enables full menu visibility */
  isUserAdmin?: boolean;
  /**
   * Force the navigation mode instead of inferring it from the route. Used by role-specific
   * shells (provider/advisor layouts) that wrap the shared `MobileNav`: the plural public
   * profile route `/providers/:id/profile` does not start with `/provider/`, so without this
   * the mobile menu would fall back to investor mode inside a provider/advisor shell.
   */
  modeOverride?: UserMode;
}

/**
 * Custom hook that provides navigation menu items based on current route
 * and applies consistent filtering logic across desktop and mobile views.
 * 
 * This is the single source of truth for navigation state, ensuring that
 * both Sidebar (desktop) and MobileNav show identical menu items.
 * 
 * @param options - Configuration options including user admin status
 * @returns {NavigationState} Navigation state including menu items, bottom items, and mode flags
 */
export const useNavigation = (options: UseNavigationOptions = {}): NavigationState => {
  const { isUserAdmin = false, modeOverride } = options;
  const location = useLocation();

  // Determine current mode. When a shell forces the mode (modeOverride), that wins; otherwise infer
  // from the route. Match the provider portal precisely (trailing slash) so the public profile route
  // `/providers/:id/profile` is NOT mistaken for the provider portal.
  const routeProviderMode = location.pathname.startsWith("/provider/");
  const routeAdvisorMode = location.pathname.startsWith("/advisor");
  const routeAdminMode = location.pathname.startsWith("/admin");

  const isProviderMode = modeOverride ? modeOverride === "provider" : routeProviderMode;
  const isAdvisorMode = modeOverride ? modeOverride === "advisor" : routeAdvisorMode;
  const isAdminMode = modeOverride ? modeOverride === "admin" : routeAdminMode;
  const isInvestorMode = modeOverride
    ? modeOverride === "investor"
    : !routeProviderMode && !routeAdvisorMode && !routeAdminMode;

  // Determine user mode
  let mode: UserMode = "investor";
  if (isProviderMode) mode = "provider";
  else if (isAdvisorMode) mode = "advisor";
  else if (isAdminMode) mode = "admin";

  // Select appropriate menu items
  let menuItems = navigationConfig.mainMenuItems;
  let bottomMenuItems = navigationConfig.mainBottomMenuItems;

  if (isProviderMode) {
    menuItems = navigationConfig.providerMenuItems;
    bottomMenuItems = navigationConfig.providerBottomMenuItems;
  } else if (isAdvisorMode) {
    menuItems = navigationConfig.advisorMenuItems;
    bottomMenuItems = navigationConfig.advisorBottomMenuItems;
  } else if (isAdminMode) {
    menuItems = navigationConfig.adminMenuItems;
    bottomMenuItems = navigationConfig.mainBottomMenuItems;
  } else if (isInvestorMode) {
    menuItems = navigationConfig.mainMenuItems;
    if (isUserAdmin) {
      bottomMenuItems = navigationConfig.mainBottomMenuItems;
    } else {
      bottomMenuItems = navigationConfig.mainBottomMenuItems.filter(
        item => item.id !== "notifications"
      );
    }
  }

  return {
    menuItems,
    bottomMenuItems,
    mode,
    isProviderMode,
    isAdvisorMode,
    isAdminMode,
    isInvestorMode,
  };
};
